import { Sprite, Container, Rectangle, Point } from 'pixi.js';
import { Game } from '../../game';
import { Scene } from '../Scene';
import { StoryScene } from '../03-story/storyScene';
import { NextLevelDialog } from './NextLevelDialog';
import { BatTally } from './BatTally';
import { DisposableGroup, Disposable, Music, SoundEffect, randInt } from '../../utils';
import { GAMEPLAY } from '../../constants';
import { Button, FocusManager, InteractiveWrapper } from '../../ui';
import KeyboardJS from 'keyboardjs';
import * as Hammer from 'hammerjs';
import * as PixiHammerConnector from 'pixi-hammer';

/** Helper point for calculations, like getting gesture events into global coordinates */
const HelperPoint = new Point();

export class ResultScene extends Scene
{
    // saving state inputs
    private levelNum: number;
    private wasSuccess: boolean;
    // UI
    private background: Sprite;
    private batTally: BatTally;
    private nextLevel: NextLevelDialog;
    private settings: Button;
    private gestureCatcher: Container;

    private music: Music;
    private hammer: PixiHammerConnector;
    private disposables: DisposableGroup;
    private batEffect: SoundEffect;
    private squakEffect: SoundEffect;

    constructor(game:Game, success:boolean, level:number)
    {
        super(game);

        this.wasSuccess = success;
        this.levelNum = level;

        this.disposables = new DisposableGroup();
    }

    async preload()
    {
        await super.preload();
        const {loader} = this;
        loader.add('resultBG', './assets/result/bg_rewardscreens{size}.jpg');
        loader.add('music', './assets/music/Cyb120_Final_Stereo_Music_20191206_EndLevel.{opus,caf,mp3}');
        loader.add('squak', './assets/sfx/squakbox_pick_up_beep.mp3');
        loader.add('bats', `./assets/sfx/BatFlaps${randInt(1, 3)}.{opus,caf,mp3}`);
        BatTally.preload();
        NextLevelDialog.preload();
        Button.preload();

        await loader.load();

        this.setup();
    }

    private setup()
    {
        const {loader} = this;
        this.background = new Sprite(loader.getTexture('resultBG'));
        this.addChild(this.background);

        this.gestureCatcher = new Container();
        this.addChild(this.gestureCatcher);
        this.gestureCatcher.interactive = true;
        this.gestureCatcher.hitArea = new Rectangle(0, 0, 2160, 1080);

        // reward with bats!
        const progress = this.game.userProgress;
        if (this.wasSuccess)
        {
            progress.batsRescued += GAMEPLAY.LEVELS[progress.currentLevel].BAT_REWARD;
            if (progress.batsRescued > GAMEPLAY.TOTAL_BATS)
            {
                progress.batsRescued = GAMEPLAY.TOTAL_BATS;
            }
            this.game.saveProgress();
            
            // Analytics; user given the opportunity to level up.
            this.game.logEvent("level_up", progress.currentLevel.toString() );
        }
        // if they lost the level, then reduce the last played level by 1 in case they don't
        // use one of the next level buttons and refresh or leave and come back later
        else
        {
            // Analytics; user not given the opportunity to level up.
            this.game.logEvent("endLevel", progress.currentLevel.toString() );
            
            progress.currentLevel--;
            this.game.saveProgress();
        }

        const remainingBats = GAMEPLAY.TOTAL_BATS - progress.batsRescued;

        // continue setup
        this.nextLevel = new NextLevelDialog(this.levelNum, this.wasSuccess, remainingBats);
        this.nextLevel.position.set((2160 - 1438) / 2, (1080 - 807) / 2);
        this.addChild(this.nextLevel);
        this.nextLevel.on('level', (adjustment: number) => {
            let eventName: string = "choose_same_level";
            if (adjustment > 0) {
                eventName = "choose_harder_level";
            } else if (adjustment < 0) {
                eventName = "choose_easier_level";
            }
            this.game.logEvent(eventName);
            
            this.game.vo.stop();
            FocusManager.instance.popContext();
            // because we modified the current level above if it was not a success, we need to
            // add that number back in so that the buttons do the expected thing.
            this.game.nextLevel(adjustment + (this.wasSuccess ? 0 : 1));
        });
        if (this.nextLevel.nextLevel)
        {
            this.disposables.add(new InteractiveWrapper(this.nextLevel.nextLevel, {
                onHover: () => {
                    this.game.vo.playHighlight(['CYB_Game_NextLevel', `CYB_Game_${this.nextLevel.nextLevelBatCount}`, 'CYB_Game_Bats'], false);
                },
                onTap: () => {

                }
            }));
        }
        if (this.nextLevel.sameLevel)
        {
            this.disposables.add(new InteractiveWrapper(this.nextLevel.sameLevel, {
                onHover: () => {
                    this.game.vo.playHighlight(['CYB_Game_SameLevel', `CYB_Game_${this.nextLevel.sameLevelBatCount}`, 'CYB_Game_Bats'], false);
                },
                onTap: () => {

                }
            }));
        }
        if (this.nextLevel.prevLevel)
        {
            this.disposables.add(new InteractiveWrapper(this.nextLevel.prevLevel, {
                onHover: () => {
                    this.game.vo.playHighlight(['CYB_Game_BackLevel', `CYB_Game_${this.nextLevel.prevLevelBatCount}`, 'CYB_Game_Bats'], false);
                },
                onTap: () => {

                }
            }));
        }

        if (this.wasSuccess)
        {
            this.batTally = new BatTally(progress.currentLevel, progress.batsRescued);
            this.addChild(this.batTally);
            this.batTally.on('exit', () => {
                // Analytics.
                this.game.logEvent("endGame");
                if (progress.batsRescued >= GAMEPLAY.TOTAL_BATS)
                {
                    // Game has been successfully completed.
                    // If current level is 6, reset level to 0 for next time.
                    if (progress.currentLevel == 6) {
                        progress.currentLevel = 0;
                        this.game.saveProgress();
                    }

                    this.game.logEvent("final_cutscene");
                    const nextScene = new StoryScene( this.game, false );
                    this.game.app.state.scene.value = nextScene;
                }
                else
                {
                    this.game.returnToTitle();
                }
            });
            this.batTally.on('continue', () => this.openNextLevel());
            this.disposables.add(new InteractiveWrapper(this.batTally.endGame, {
                onHover: () => {
                    this.game.vo.playHighlight(/*progress.batsRescued >= GAMEPLAY.TOTAL_BATS ? 'CYB_End_MissionAccomplished' :*/ 'CYB_Game_Transition3');
                },
                onTap: () => {}
            }));
            if (this.batTally.continue)
            {
                this.disposables.add(new InteractiveWrapper(this.batTally.continue, {
                    onHover: () => {
                        this.game.vo.playHighlight('CYB_Game_Transition4');
                    },
                    onTap: () => {}
                }));
            }
        }
        else
        {
            this.nextLevel.visible = true;
        }

        this.settings = new Button(loader.getTexture('btn_settings', 'SharedUI'));
        this.addChild(this.settings);
        this.settings.position.set(1625, 1014);
        this.disposables.add(new InteractiveWrapper(this.settings, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_Game_Settings');
            },
            onTap: () => {
                this.openSettings();
            }
        }));

        KeyboardJS.setContext('results');
        this.disposables.add(Disposable.fromKey('space', (e: KeyboardJS.KeyEvent) => {
            e.preventRepeat();
            // don't play AD if game is busy doing something
            if (this.game.vo.playing)
            {
                return;
            }
            if (this.shouldPlayAD)
            {
                this.playSceneAD();
            }
        }));

        this.hammer = new PixiHammerConnector(
            this.game.pixi.view,
            this.game.pixi.renderer.plugins.interaction,
            new Hammer(this.game.pixi.view, {
                recognizers: [
                    [Hammer.Swipe, {
                        direction: Hammer.DIRECTION_HORIZONTAL | Hammer.DIRECTION_VERTICAL,
                        velocity: 0.25, // default is 0.3 (px/ms)
                    }],
                    [Hammer.Tap]
                ]
            })
        );
        this.hammer.registerHandlerTypes(['tap']);
        this.gestureCatcher.on('hammer-tap', (ev: HammerInput) => {
            // get DOM position into PIXI global position
            this.game.pixi.renderer.plugins.interaction.mapPositionToPoint(HelperPoint, ev.center.x, ev.center.y);
            if (!this.game.vo.playing && this.shouldPlayAD && HelperPoint.y >= 540)
            {
                this.playSceneAD();
            }
        });

        this.music = new Music(loader.getSound('music'), this.game);
        this.squakEffect = new SoundEffect(loader.getSound('squak'), this.game);
        this.batEffect = new SoundEffect(loader.getSound('bats'), this.game);
        this.disposables.add(this.music, this.squakEffect, this.batEffect);
    }

    public start()
    {
        this.music.play();
        if (this.wasSuccess)
        {
            this.openTally();
        }
        else
        {
            this.openNextLevel();
        }
    }

    private async playSceneAD()
    {
        if (this.batTally?.visible)
        {
            await this.game.vo.playVO([this.game.userProgress.batsRescued >= GAMEPLAY.TOTAL_BATS ? 'CYB_Game_TallyEND_AD' : 'CYB_Game_Tally_AD', 'CYB_GameIntroOutro_Settings']);
        }
        else if (this.nextLevel.visible)
        {
            await this.game.vo.playVO('CYB_Game_NextLevelAD');
            if (this.nextLevel.nextLevel)
            {
                await this.game.vo.playVO('CYB_Game_NextLevel_AD');
            }
            else
            {
                await this.game.vo.playVO('CYB_Game_SameLevel_AD');
            }
        }
    }

    private async openTally()
    {
        this.batEffect.play();
        this.batTally.open();
        FocusManager.instance.activateContext(this.batTally.focus.concat([this.settings]));
        const rescued = this.game.userProgress.batsRescued;
        const remaining = GAMEPLAY.TOTAL_BATS - rescued;
        if (rescued === GAMEPLAY.TOTAL_BATS)
        {
            await this.game.vo.playVO('CYB_End_WellDone', true);
        }
        else
        {
            await this.game.vo.playVO(['CYB_Game_Transition1_part1', `CYB_Tally_${rescued}`, 'CYB_Game_Transition1_part2'], false);
            if (remaining > 0)
            {
                await this.game.vo.playVO([`CYB_Tally_${remaining}`, 'CYB_Game_Transition1_part3'], false);
                await this.game.vo.playVO('CYB_Game_Transition2', true);
            }
        }
        this.batTally.highlight();
        // stop higlight VO from changing focus
        this.game.vo.stop();
        if (this.shouldPlayAD)
        {
            await this.playSceneAD();
        }
        if (this.shouldPlayAD && remaining === 0)
        {
            await this.game.vo.playVO('CYB_End_Mission_AD', true);
        }
    }

    private async openNextLevel()
    {
        this.game.vo.stop();
        if (this.batTally)
        {
            this.batTally.visible = false;
        }
        FocusManager.instance.popContext();

        this.squakEffect.play();
        this.nextLevel.open();
        FocusManager.instance.activateContext(this.nextLevel.focus.concat([this.settings]));
        let vo:string[] = [];
        if (this.wasSuccess)
        {
            vo.push(`CYB_Game_Wall_NextLevel_${this.levelNum}`);
        }
        else
        {
            // basic "what would you like to do next?", in lieu of something actually written for it
            vo.push('CYB_Game_Wall_NextLevel_Alt2');
        }
        if (vo.length)
        {
            await this.game.vo.playVO(vo, false);
        }
        this.nextLevel.highlight();
        // stop higlight VO from changing focus
        this.game.vo.stop();
        if (this.shouldPlayAD)
        {
            await this.playSceneAD();
        }
    }

    private openSettings()
    {
        this.game.vo.stop();

        const hadAD = this.shouldPlayAD;
        KeyboardJS.setContext('settings');
        this.game.showAccessibilityPopover(() => {
            KeyboardJS.setContext('results');
            // if audio descriptions were turned on, play the level description now
            if (!hadAD && this.shouldPlayAD)
            {
                this.playSceneAD();
            }
        });
    }

    public update(_elapsed:number)
    {
    }

    public cleanup()
    {
        KeyboardJS.setContext('global');
        this.disposables.destroy();
        this.hammer.destroy();
        super.cleanup();
        super.destroy({children: true});
    }
}